import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题和副标题样式已删除 - 当前未使用

// 横幅区域样式
const Banner = styled('div', {
  width: '100%',
  height: '400px',
  background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/assets/nft-farm-bg.jpg") center/cover no-repeat',
    opacity: 0.2,
    zIndex: 0
  }
});

// 横幅内容样式
const BannerContent = styled('div', {
  zIndex: 1,
  textAlign: 'center',
  color: 'white'
});

// 横幅标题样式
const BannerTitle = styled('h2', {
  fontSize: '3rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 横幅描述样式
const BannerDescription = styled('p', {
  fontSize: '1.25rem',
  maxWidth: '800px',
  marginBottom: '2rem'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 2rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      },
      secondary: {
        background: 'transparent',
        color: 'white',
        border: '2px solid white',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 区块样式
const Section = styled('section', {
  marginBottom: '4rem'
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  color: '$textSecondary',
  maxWidth: '800px'
});

// 特点部分容器样式
const FeaturesContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2rem',
  marginTop: '2rem'
});

// 特点卡片样式
const FeatureCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// 特点图标样式
const FeatureIcon = styled('div', {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: '$accentPrimary',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '1rem'
});

// 特点标题样式
const FeatureTitle = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 特点描述样式
const FeatureDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 流程步骤容器样式
const StepsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  marginTop: '2rem'
});

// 步骤卡片样式
const StepCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  display: 'flex',
  alignItems: 'center',
  gap: '2rem',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '1rem',
    textAlign: 'center'
  }
});

// 步骤数字样式
const StepNumber = styled('div', {
  width: '4rem',
  height: '4rem',
  borderRadius: '50%',
  background: '$accentPrimary',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: 'white',
  flexShrink: 0
});

// 步骤内容样式
const StepContent = styled('div', {
  flex: 1
});

// 步骤标题样式
const StepTitle = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 步骤描述样式
const StepDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// NFT展示区域容器样式
const NftShowcaseContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '1.5rem',
  marginTop: '2rem'
});

// NFT卡片样式
const NftCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  overflow: 'hidden',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// NFT图片容器样式
const NftImageContainer = styled('div', {
  width: '100%',
  paddingTop: '100%', // 1:1 比例
  position: 'relative'
});

// NFT图片样式
const NftImage = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'center/cover no-repeat'
});

// NFT内容样式
const NftContent = styled('div', {
  padding: '1rem'
});

// NFT名称样式
const NftName = styled('h4', {
  fontSize: '1rem',
  fontWeight: 'bold',
  marginBottom: '0.25rem',
  color: '$textPrimary'
});

// NFT收益样式
const NftReward = styled('p', {
  fontSize: '0.9rem',
  color: '$accentPrimary',
  fontWeight: 'bold'
});

// 呼叫行动区块样式
const CtaSection = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '3rem',
  textAlign: 'center',
  marginTop: '3rem'
});

/**
 * NFT矿场概览页面组件
 * 提供NFT挖矿概念介绍与功能概览
 */
const NftFarmOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 特点数据数组
  const features = [
    {
      icon: '🖼️',
      title: t('nftFarm.features.uniqueNFTs.title', '独特NFT收藏品'),
      description: t('nftFarm.features.uniqueNFTs.description', '持有限量版的矿工NFT，每个都有独特的视觉设计和挖矿属性')
    },
    {
      icon: '⛏️',
      title: t('nftFarm.features.passiveIncome.title', '被动挖矿收益'),
      description: t('nftFarm.features.passiveIncome.description', '您的NFT自动为您产生加密货币收益，无需额外的硬件投入')
    },
    {
      icon: '🔄',
      title: t('nftFarm.features.upgradeSystem.title', '进化升级系统'),
      description: t('nftFarm.features.upgradeSystem.description', '通过融合和升级提升您的NFT属性和挖矿效率')
    },
    {
      icon: '🏆',
      title: t('nftFarm.features.rewards.title', '多重奖励机制'),
      description: t('nftFarm.features.rewards.description', '参与特殊挖矿事件和社区活动获取额外奖励和稀有NFT')
    }
  ];
  
  // 流程步骤数据
  const steps = [
    {
      number: 1,
      title: t('nftFarm.steps.purchaseNFT.title', '购买矿工NFT'),
      description: t('nftFarm.steps.purchaseNFT.description', '从我们的收藏品市场中选择并购买您喜欢的矿工NFT。每个NFT都具有不同的挖矿属性和视觉设计。')
    },
    {
      number: 2,
      title: t('nftFarm.steps.stake.title', '质押您的NFT'),
      description: t('nftFarm.steps.stake.description', '将您的NFT质押到矿场中，开始产生被动挖矿收益。质押期间，NFT将锁定在您的账户中。')
    },
    {
      number: 3,
      title: t('nftFarm.steps.earn.title', '获取挖矿收益'),
      description: t('nftFarm.steps.earn.description', '根据您NFT的属性和稀有度，每天获得对应的加密货币奖励。收益将自动累积到您的账户中。')
    },
    {
      number: 4,
      title: t('nftFarm.steps.upgrade.title', '升级您的NFT'),
      description: t('nftFarm.steps.upgrade.description', '使用挖矿收益购买升级材料，或者融合多个NFT来提升您的矿工属性，获得更高的挖矿效率。')
    }
  ];
  
  // 展示NFT数据
  const showcaseNfts = [
    {
      id: 1,
      name: 'Golden Miner #042',
      image: '/assets/nft-golden-miner.jpg',
      reward: '+0.005 BTC/日'
    },
    {
      id: 2,
      name: 'Diamond Driller #137',
      image: '/assets/nft-diamond-driller.jpg',
      reward: '+0.008 BTC/日'
    },
    {
      id: 3,
      name: 'Quantum Digger #089',
      image: '/assets/nft-quantum-digger.jpg',
      reward: '+0.012 BTC/日'
    },
    {
      id: 4,
      name: 'Stellar Excavator #251',
      image: '/assets/nft-stellar-excavator.jpg',
      reward: '+0.015 BTC/日'
    }
  ];
  
  return (
    <PageContainer>
      <Banner>
        <BannerContent>
          <BannerTitle>{t('nftFarm.banner.title', 'NFT矿场')}</BannerTitle>
          <BannerDescription>
            {t('nftFarm.banner.description', '通过持有独特的NFT矿工获得被动挖矿收益，无需物理矿机设备')}
          </BannerDescription>
          <Link to="/products/nft-farm/collections">
            <Button variant="primary">
              {t('nftFarm.banner.exploreNFTs', '探索NFT收藏品')}
            </Button>
          </Link>
        </BannerContent>
      </Banner>
      
      <Section>
        <SectionTitle>{t('nftFarm.intro.title', '什么是NFT矿场')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.intro.description', 'NFT矿场是一种创新的挖矿模式，将NFT收藏品与加密货币挖矿相结合。通过持有特定的矿工NFT，您可以获得相应的挖矿权益，系统会自动为您生成加密货币收益，无需购买和维护实体矿机设备。每个NFT都有独特的属性和稀有度，决定其挖矿效率和收益率。')}
        </SectionDescription>
        
        <FeaturesContainer>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesContainer>
      </Section>
      
      <Section>
        <SectionTitle>{t('nftFarm.howItWorks.title', 'NFT挖矿如何运作')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.howItWorks.description', 'NFT挖矿是一种创新的机制，允许用户通过持有和质押NFT来获得加密货币奖励。我们的系统将传统的挖矿过程虚拟化，并与NFT收藏品的所有权绑定。')}
        </SectionDescription>
        
        <StepsContainer>
          {steps.map((step, index) => (
            <StepCard key={index}>
              <StepNumber>{step.number}</StepNumber>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </StepCard>
          ))}
        </StepsContainer>
      </Section>
      
      <Section>
        <SectionTitle>{t('nftFarm.showcase.title', '精选NFT矿工')}</SectionTitle>
        <SectionDescription>
          {t('nftFarm.showcase.description', '探索我们的顶级NFT矿工收藏品，每个NFT都具有独特的视觉设计和挖矿属性。稀有度越高的NFT，产生的挖矿收益也越丰厚。')}
        </SectionDescription>
        
        <NftShowcaseContainer>
          {showcaseNfts.map(nft => (
            <NftCard key={nft.id}>
              <NftImageContainer>
                <NftImage style={{ background: `url(${nft.image || '/assets/nft-placeholder.jpg'}) center/cover no-repeat` }} />
              </NftImageContainer>
              <NftContent>
                <NftName>{nft.name}</NftName>
                <NftReward>{nft.reward}</NftReward>
              </NftContent>
            </NftCard>
          ))}
        </NftShowcaseContainer>
      </Section>
      
      <CtaSection>
        <SectionTitle>{t('nftFarm.cta.title', '开始您的NFT挖矿之旅')}</SectionTitle>
        <SectionDescription style={{ margin: '0 auto 2rem' }}>
          {t('nftFarm.cta.description', '立即探索我们的NFT收藏品，获取您的第一个矿工NFT，开始赚取被动挖矿收益。')}
        </SectionDescription>
        <Link to="/products/nft-farm/collections">
          <Button variant="primary">
            {t('nftFarm.cta.exploreNFTs', '探索NFT收藏品')}
          </Button>
        </Link>
      </CtaSection>
    </PageContainer>
  );
};

export default NftFarmOverview; 